/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 * - withCurrencyCode - Appends the ISO currency code to an ambiguous "$" price.
 */

const MONEY_FORMAT = '${{amount}}'

/**
 * Formats a number with the specified delimiters
 * @param {number} number - The number to format
 * @param {number} [precision=2] - Number of decimal places
 * @param {string} [thousands=','] - Thousands separator
 * @param {string} [decimal='.'] - Decimal separator
 * @returns {string} Formatted number
 */
const formatWithDelimiters = (
  number: number,
  precision: number = 2,
  thousands: string = ',',
  decimal: string = '.'
): string => {
  if (isNaN(number) || number == null) {
    return '0'
  }

  const fixedNumber = (number / 100.0).toFixed(precision)
  const [dollars, cents] = fixedNumber.split('.')

  const formattedDollars = dollars.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`)
  const formattedCents = cents ? `${decimal}${cents}` : ''

  return `${formattedDollars}${formattedCents}`
}

/**
 * Format money values based on your shop currency settings
 * @param {number|string} cents - Value in cents or dollar amount e.g. 300 cents or 3.00 dollars
 * @param {string} [format] - Shop money_format setting
 * @returns {string} Formatted value
 */
const formatMoney = (cents: number | string, format: string = MONEY_FORMAT): string => {
  const cleanCents = typeof cents === 'string' ? parseInt(cents.replace('.', '')) : cents
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const formatMap = {
    amount: () => formatWithDelimiters(cleanCents, 2),
    amount_no_decimals: () => formatWithDelimiters(cleanCents, 0),
    amount_with_comma_separator: () => formatWithDelimiters(cleanCents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => formatWithDelimiters(cleanCents, 0, '.', ',')
  }

  let formatType: keyof typeof formatMap = 'amount'
  const match = format.match(placeholderRegex)

  if (match !== null && match[1] in formatMap) {
    formatType = match[1] as keyof typeof formatMap
  }

  const value = formatMap[formatType]?.() ?? formatWithDelimiters(cleanCents, 2)
  return format.replace(placeholderRegex, value)
}

/**
 * Appends the ISO code to a formatted price when the symbol is ambiguous (any
 * "$" market other than the store's own currency), so shoppers can tell USD,
 * CAD or NZD from the default
 * @param {string} formatted - Output of formatMoney
 * @param {string} symbol - Active market currency symbol
 * @param {string} isoCode - Active market currency ISO code
 * @param {string} [defaultIsoCode] - Store's default currency ISO code
 * @returns {string} Formatted value, with the ISO code where needed
 */
const withCurrencyCode = (
  formatted: string,
  symbol?: string,
  isoCode?: string,
  defaultIsoCode?: string
): string => {
  if (!formatted || symbol !== '$' || !isoCode) return formatted
  if (isoCode === defaultIsoCode) return formatted
  return `${formatted} ${isoCode}`
}

export { formatMoney, withCurrencyCode }
